import React, { useState, useEffect } from 'react';
import { GetQuestions, SubmitQuiz, GetSettings, TrackMetric, CheckDuplicateParticipant } from '../../../utilities/BL/RubaQuiz';

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA DINÁMICA
1. Organizador de la dinámica: La empresa Ruba es la única responsable organizadora de esta dinámica y de la entrega física de los premios correspondientes.
2. Deslinde de responsabilidad técnica: Interaktia actúa únicamente como el proveedor tecnológico de la plataforma del juego y queda eximido de cualquier responsabilidad derivada de la entrega de premios o controversias de la dinámica.
3. Participantes: Dinámica válida únicamente para aficionados presentes en el estadio que sean mayores de edad.
4. Registro: Cada participante puede registrarse una sola vez. Intentos múltiples serán anulados.
5. Elección del ganador: Los ganadores se elegirán con base en la puntuación más alta de aciertos y, en caso de empate, por el menor tiempo de respuesta registrado por el servidor.
6. Decisiones: Las decisiones del comité organizador (Ruba) son inapelables y definitivas.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono) serán recabados con la única finalidad de registrar su participación en la "Trivia Ruba", poder contactarle y validar su identidad en caso de resultar ganador, así como para estadísticas internas de participación de los organizadores.

Al aceptar este aviso, autoriza expresamente que sus datos de contacto sean compartidos y transferidos entre la empresa Ruba, el Club Xolos de Tijuana, la empresa operadora Interaktia, así como sus asociados comerciales y aliados estratégicos con fines de marketing, estadísticas y entrega de premios. No se realizarán transferencias a otros terceros sin su consentimiento. Para dudas o aclaraciones contacte a soporte.`;

const SOURCE_MAPPING = {
    obs: 'overlay_screen',
    seat: 'seats',
    bath: 'restrooms'
};

// Fisher-Yates Shuffle algorithm
function shuffleArray(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

export function RubaQuizFan() {
    const [phase, setPhase] = useState('register'); // register, quiz, submitting, finished, error, ended
    const [questions, setQuestions] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    
    // Form fields
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    
    // Validation Errors
    const [validationErrors, setValidationErrors] = useState({});
    
    // Modals
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // States for progress flow
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [selectedOptionsList, setSelectedOptionsList] = useState([]); // Array of selected option IDs
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [formStartTracked, setFormStartTracked] = useState(false);

    // Generate or retrieve Session ID
    const getSessionId = () => {
        let sessId = sessionStorage.getItem('ruba_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('ruba_session_id', sessId);
        }
        return sessId;
    };

    const trackEvent = async (eventName) => {
        const sessId = getSessionId();
        const urlParams = new URLSearchParams(window.location.search);
        const rawSrc = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src') || 'direct';
        const srcVal = SOURCE_MAPPING[rawSrc] || rawSrc;
        try {
            await TrackMetric(eventName, sessId, srcVal);
        } catch (e) {
            console.error('Local tracking error:', e);
        }
        try {
            if (window.gtag) {
                window.gtag('event', `ruba_${eventName}`, {
                    session_id: sessId,
                    event_category: 'ruba_trivia',
                    event_label: eventName,
                    traffic_source: srcVal
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    };

    // Fetch settings and questions from backend
    useEffect(() => {
        const loadInitialData = async () => {
            const savedProgressStr = sessionStorage.getItem('ruba_quiz_progress');
            let hasProgress = false;
            let parsedProgress = null;

            if (savedProgressStr) {
                try {
                    parsedProgress = JSON.parse(savedProgressStr);
                    if (parsedProgress && parsedProgress.sessionId) {
                        sessionStorage.setItem('ruba_session_id', parsedProgress.sessionId);
                        hasProgress = true;
                    }
                } catch (e) {
                    console.error('Error parsing progress:', e);
                }
            }

            if (!hasProgress) {
                const existingSessId = sessionStorage.getItem('ruba_session_id');
                if (!existingSessId) {
                    // Fresh start! Generate a new session ID and clear tracking flags
                    sessionStorage.removeItem('ruba_page_view_tracked');
                    sessionStorage.removeItem('ruba_qr_scan_tracked');
                    const newSessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
                    sessionStorage.setItem('ruba_session_id', newSessId);
                }
            }

            if (!sessionStorage.getItem('ruba_page_view_tracked')) {
                sessionStorage.setItem('ruba_page_view_tracked', 'true');
                trackEvent('page_view');
            }

            const urlParams = new URLSearchParams(window.location.search);
            const hasQrParam = urlParams.get('s') || urlParams.get('src') === 'qr';
            if (hasQrParam && !sessionStorage.getItem('ruba_qr_scan_tracked')) {
                sessionStorage.setItem('ruba_qr_scan_tracked', 'true');
                trackEvent('qr_scan');
            }

            // Check active status
            const settingsRes = await GetSettings();
            let isActive = true;
            if (settingsRes.success && settingsRes.data) {
                isActive = settingsRes.data.isTriviaActive;
                
                // If inactive and no progress is saved, block registration
                const savedProgress = sessionStorage.getItem('ruba_quiz_progress');
                if (!isActive && !savedProgress) {
                    setPhase('ended');
                    return;
                }
            }

            const res = await GetQuestions();
            if (res.success && res.data) {
                const savedProgress = sessionStorage.getItem('ruba_quiz_progress');
                if (savedProgress) {
                    try {
                        const parsed = JSON.parse(savedProgress);
                        if (parsed.email && parsed.phone) {
                            setName(parsed.name || '');
                            setEmail(parsed.email || '');
                            setPhone(parsed.phone || '');
                            setAcceptedTerms(true);
                            setCurrentIdx(parsed.currentIndex || 0);
                            setSelectedOptionsList(parsed.answers || []);
                            
                            // Load saved question order to prevent breaking index mapping
                            if (parsed.questionOrder) {
                                const orderedQuestions = parsed.questionOrder.map(qId => 
                                    res.data.find(q => q.Id === qId || q.id === qId)
                                ).filter(Boolean);
                                setQuestions(orderedQuestions);
                                setPhase('quiz');
                                return;
                            }
                        }
                    } catch (err) {
                        sessionStorage.removeItem('ruba_quiz_progress');
                    }
                }

                // Shuffle questions and options for a new session
                const shuffledQuestions = shuffleArray(res.data);
                shuffledQuestions.forEach(q => {
                    const keepOrder = q.keepOptionsOrder || q.KeepOptionsOrder;
                    if (!keepOrder) {
                        q.options = shuffleArray(q.options);
                    }
                });
                setQuestions(shuffledQuestions);
            } else {
                setPhase('error');
                setApiErrorMessage(res.message || 'No se pudieron cargar las preguntas.');
            }
        };
        loadInitialData();
    }, []);

    const handleStartQuiz = async (e) => {
        e.preventDefault();
        setValidationErrors({});

        const errors = {};
        if (!name.trim()) {
            errors.name = 'El nombre completo es requerido.';
        }

        // Email validation regex (supports complex subdomains)
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email.trim()) {
            errors.email = 'El correo electrónico es requerido.';
        } else if (!emailRegex.test(email)) {
            errors.email = 'El correo electrónico no tiene un formato válido.';
        }

        // Phone validation regex (optional '+' and 10 to 15 digits)
        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!phone.trim()) {
            errors.phone = 'El teléfono / WhatsApp es requerido.';
        } else if (!phoneRegex.test(phone)) {
            errors.phone = 'El teléfono debe ser numérico y contener entre 10 y 15 dígitos.';
        }

        if (!acceptedTerms) {
            errors.acceptedTerms = 'Debes aceptar los términos y el aviso de privacidad para participar.';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        if (questions.length === 0) {
            setValidationErrors({ name: 'Las preguntas están cargando, por favor espera un momento.' });
            return;
        }

        // Check duplicate participant before starting the quiz
        setPhase('submitting');
        const dupRes = await CheckDuplicateParticipant(email, phone);
        if (dupRes.success && dupRes.exists) {
            setPhase('already_participated');
            sessionStorage.removeItem('ruba_quiz_progress'); // Clear temporary progress
            sessionStorage.removeItem('ruba_session_id'); // Clear session ID
            return;
        } else if (!dupRes.success) {
            setPhase('register');
            setValidationErrors({ name: dupRes.message || 'Error al validar tus datos de registro.' });
            return;
        }

        // Initialize session storage with progress and question order
        const qOrder = questions.map(q => q.id || q.Id);
        const currentSessionId = getSessionId();
        sessionStorage.setItem('ruba_quiz_progress', JSON.stringify({
            name,
            email,
            phone,
            currentIndex: 0,
            answers: [],
            questionOrder: qOrder,
            sessionId: currentSessionId
        }));

        trackEvent('form_complete');
        trackEvent('quiz_start');

        setPhase('quiz');
    };

    const handleSelectOption = (optionId) => {
        setSelectedOptionId(optionId);
    };

    const handleNextQuestion = async () => {
        if (selectedOptionId === null) return;

        const updatedAnswers = [...selectedOptionsList, selectedOptionId];
        setSelectedOptionsList(updatedAnswers);
        setSelectedOptionId(null);

        const qOrder = questions.map(q => q.id || q.Id);

        // Update progress in sessionStorage
        const currentSessionId = getSessionId();
        sessionStorage.setItem('ruba_quiz_progress', JSON.stringify({
            name,
            email,
            phone,
            currentIndex: currentIdx + 1,
            answers: updatedAnswers,
            questionOrder: qOrder,
            sessionId: currentSessionId
        }));

        if (currentIdx < questions.length - 1) {
            setCurrentIdx(currentIdx + 1);
        } else {
            // Submit quiz
            setPhase('submitting');
            const res = await SubmitQuiz(name, email, phone, updatedAnswers, acceptedTerms);
            if (res.success) {
                setPhase('finished');
                trackEvent('quiz_complete');
                sessionStorage.removeItem('ruba_quiz_progress'); // Clear progress on success
                sessionStorage.removeItem('ruba_session_id'); // Clear session ID on success
            } else if (res.code === 'ALREADY_PARTICIPATED') {
                setPhase('already_participated');
                sessionStorage.removeItem('ruba_quiz_progress'); // Clear progress
                sessionStorage.removeItem('ruba_session_id'); // Clear session ID
            } else {
                setPhase('error');
                setApiErrorMessage(res.message || 'Error al procesar el envío de la trivia.');
            }
        }
    };

    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    const currentQuestion = questions[currentIdx];

    return (
        <main className="container RubaQuizFan my-4 d-flex flex-column align-items-center justify-content-between min-vh-100 px-3">
            {/* Legal Modals */}
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            {/* Header / Brand */}
            <header className="w-100 text-center mt-3">
                <img 
                    src="/img/page/xolos-five-bicolor.png" 
                    className="img-fluid d-block mx-auto mb-3" 
                    style={{ maxHeight: '75px', objectFit: 'contain' }}
                    alt="Interaktia" 
                />
            </header>

            {/* Core Card Section */}
            <section className="w-100 flex-grow-1 d-flex flex-column my-4" style={{ maxWidth: '480px' }}>
                <div className="card border-0 p-4 bg-white rounded-4">
                    
                    {phase === 'register' && (
                        <form onSubmit={handleStartQuiz}>
                            <h2 className="text-center fw-bold mb-4 text-dark fs-3">Registro a la trivia <span className='d-block p-2'><img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' style={{ width: '280px' }} /></span></h2>
                            {/* <p className="text-muted text-center mb-4 small">
                                Ingresa tus datos para comenzar a participar.
                            </p> */}

                            <div className="mb-3">
                                <label className="form-label small fw-bold text-secondary">Nombre Completo</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.name ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    //placeholder="Ej. Juan Pérez"
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                {validationErrors.name && (
                                    <div className="invalid-feedback small">{validationErrors.name}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-bold text-secondary">Correo Electrónico</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.email ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    //placeholder="juanito@mail.gob.com.mx"
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                {validationErrors.email && (
                                    <div className="invalid-feedback small">{validationErrors.email}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-bold text-secondary">Teléfono o WhatsApp</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.phone ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    //placeholder="Ej. 6641234567"
                                    value={phone} 
                                    onChange={(e) => setPhone(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                <label className="form-check-label small text-secondary" htmlFor="acceptTermsCheck" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                    Ej. 6641234567. Incluye código de país (en caso de ser necesario)
                                </label>
                                {validationErrors.phone && (
                                    <div className="invalid-feedback small">{validationErrors.phone}</div>
                                )}
                            </div>

                            {/* Terms Agreement Checkbox */}
                            <div className="form-check mb-4 text-start">
                                <input 
                                    className={`form-check-input ${validationErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                    type="checkbox" 
                                    id="acceptTermsCheck" 
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                />
                                <label className="form-check-label small text-secondary" htmlFor="acceptTermsCheck" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                    He leído y acepto los <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la dinámica.
                                </label>
                            </div>

                            <button type="submit" className="btn btn-danger w-100 py-2.5 rounded-3 fw-bold shadow-sm">
                                Iniciar la trivia
                            </button>
                            {validationErrors.acceptedTerms && (
                                    <div className="invalid-feedback small d-block">{validationErrors.acceptedTerms}</div>
                                )}
                        </form>
                    )}

                    {phase === 'quiz' && currentQuestion && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="badge bg-danger rounded-pill px-3 py-1.5 small mx-auto">
                                    Pregunta {currentIdx + 1} de {questions.length}
                                </span>
                            </div>
                            
                            {/* Render Ruba Bicolor logo above instruction if ShowLogo flag is true */}
                            {currentQuestion.showLogo || currentQuestion.ShowLogo ? (
                                <div className="text-center mb-4">
                                    {/* <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="d-block mx-auto mb-2">
                                        <path d="M12 3L2 12H5V20H10V14H14V20H19V12H22L12 3Z" fill="#dc2626" />
                                        <path d="M8 20H16V17H8V20Z" fill="#ffeb3b" />
                                    </svg> */}
                                    <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='mx-auto p-1' style={{ width: '280px', display: 'block' }} />
                                    <h3 className="fw-bold text-dark fs-4 mt-2">
                                        {currentQuestion.instruction}
                                    </h3>
                                </div>
                            ) : (
                                <h3 className="fw-bold mb-4 text-center text-dark fs-4">
                                    {currentQuestion.instruction}
                                </h3>
                            )}

                            <div className="d-flex flex-column gap-3 mb-4">
                                {currentQuestion.options.map((option) => {
                                    const isSelected = selectedOptionId === option.id;
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => handleSelectOption(option.id)}
                                            className={`btn w-100 py-3 rounded-3 text-start px-4 transition-all fw-semibold ${
                                                isSelected 
                                                ? 'btn-danger text-white border-danger shadow' 
                                                : 'btn-outline-danger border-danger-subtle text-dark bg-transparent'
                                            }`}
                                        >
                                            {option.text}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                type="button" 
                                onClick={handleNextQuestion}
                                disabled={selectedOptionId === null}
                                className="btn btn-danger w-100 py-2.5 rounded-3 fw-bold shadow-sm transition-all"
                            >
                                {currentIdx < questions.length - 1 ? 'Siguiente' : 'Finalizar trivia'}
                            </button>
                        </div>
                    )}

                    {phase === 'submitting' && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-danger mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Procesando...</span>
                            </div>
                            <h4 className="fw-bold text-dark">Procesando</h4>
                            <p className="text-muted small">Por favor, espera un momento.</p>
                        </div>
                    )}

                    {phase === 'finished' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3 fs-3">¡Muchas gracias!</h3>
                            <p className="text-muted mb-4 px-2">
                                Has finalizado la trivia con éxito. Tus datos y respuestas se enviaron correctamente y, en caso de resultar ganador, nos pondremos en contacto contigo.
                            </p>

                            <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='p-1 mx-auto' style={{ width: '280px', display: 'block', marginTop: '1em' }} />
                            <p className="text-muted mb-1 px-2">
                                ¿Quieres más info de casas y departamentos?
                            </p>
                            <a href='https://wa.link/8azzg2' target='_Blank' className='btn btn-success' onClick={() => trackEvent('whatsapp_click')}>Quiero más información</a>
                            {/* <div className="bg-light p-3 rounded-3 mb-2 small text-secondary">
                                ¡Mucho éxito en el sorteo!
                            </div> */}
                        </div>
                    )}

                    {phase === 'already_participated' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3 fs-3">Gracias por tu participación</h3>
                            <p className="text-muted px-2">Finalizaste anteriormente la trivia con éxito.</p>
                            <p className="text-muted mb-4 px-2">Tus datos y respuestas se enviaron correctamente y, en caso de resultar ganador, nos pondremos en contacto contigo.</p>

                            <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='p-1 mx-auto' style={{ width: '280px', display: 'block', marginTop: '1em' }} />
                            <p className="text-muted mb-1 px-2 mt-3">
                                ¿Quieres más info de casas y departamentos?
                            </p>
                            <a href='https://wa.link/8azzg2' target='_Blank' className='btn btn-success' onClick={() => trackEvent('whatsapp_click')}>Quiero más información</a>
                        </div>
                    )}

                    {phase === 'ended' && (
                        <div className="text-center py-5">
                            <div className="mb-4">
                                <i className="bi bi-calendar-x-fill text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3">Trivia Cerrada</h3>
                            <p className="text-muted mb-4 px-3">
                                Lo sentimos, la trivia de Ruba ha finalizado por ahora. ¡Muchas gracias por tu interés y participación!
                            </p>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '4.5rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3">Ocurrió un inconveniente</h3>
                            <p className="text-muted mb-4 px-2">
                                {apiErrorMessage || 'No ha sido posible procesar la trivia en este momento.'}
                            </p>
                            <button 
                                type="button" 
                                className="btn btn-danger px-5 py-2 rounded-3 fw-bold shadow-sm"
                                onClick={() => {
                                    setPhase('register');
                                    sessionStorage.removeItem('ruba_quiz_progress');
                                }}
                            >
                                Reintentar
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Footer / Credits */}
            {/* <footer className="w-100 text-center py-3 border-top mt-auto text-secondary small">
                Hecho con ♥ por Interaktia | 2026
            </footer> */}

            <p className='text-center FdmFooter FooterCredits'>
                <a href='https://interaktia.com/?utm_source=quiz_ruba_web&utm_medium=footer&utm_campaign=fdm_spinsmx_s2' target='_blank' className='text-dark'>Hecho con ♥ por Interaktia | 2026</a>
            </p>
        </main>
    );
}
export default RubaQuizFan;
